import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Robot, PaperPlaneRight, ChatCircleDots, ArrowRight, Spinner } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK = [
  "1 hónap alatt felépül a garázs? Biztosan?",
  "Milyen hőszigetelést ajánlanak?",
  "Mennyi az előleg?",
  "Milyen színekből választhatok?",
];

function genSession() {
  return "web-" + Math.random().toString(36).slice(2, 10);
}

export default function Chatbot() {
  const [sessionId] = useState(genSession);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Szia! Rapidgarázs AI asszisztens vagyok. Kérdezz bátran — árakról, határidőkről, anyagokról." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Sajnos most nem tudok válaszolni. Kérjen visszahívást, és kollégám azonnal segít!" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="chatbot" data-testid="chatbot-section" className="py-20 md:py-32 bg-[#F5F5F5]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-12 items-start">
          <div>
            <span className="badge-chip" style={{ background: "#0F0F0F", color: "#FFFFFF" }}>
              AI asszisztens
            </span>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-4 leading-[0.95] mb-6">
              Kérdezz bármit —<br/>most azonnal.
            </h2>
            <p className="text-[#525252] text-base md:text-lg leading-relaxed mb-6 max-w-lg">
              Nem kell várni munkaidőre. Az AI asszisztensünk 0–24 választ ad árról, határidőről, folyamatról és anyagokról.
            </p>

            <div className="space-y-3 mb-8">
              {[
                "Azonnali válasz — nem call center",
                "Pontos információk a konfigurációjáról",
                "Ha emberi segítség kell, egy kattintás a visszahívás",
              ].map((x, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#0F0F0F] flex items-center justify-center shrink-0 mt-0.5">
                    <ChatCircleDots size={14} weight="fill" className="text-[#FFFFFF]" />
                  </div>
                  <span className="text-[#0F0F0F]">{x}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {QUICK.map((q, i) => (
                <button
                  key={i}
                  data-testid={`chatbot-quick-${i}`}
                  onClick={() => send(q)}
                  className="px-3 py-2 text-xs font-semibold bg-[#FFFFFF] border border-[#E4E4E7] hover:border-[#0F0F0F] transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#FFFFFF] border border-[#E4E4E7] flex flex-col h-[520px]">
            <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7] bg-[#0F0F0F] text-[#FFFFFF]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#FFFFFF] flex items-center justify-center rounded-sm">
                  <Robot size={18} weight="fill" className="text-[#0F0F0F]" />
                </div>
                <div>
                  <div className="font-display font-bold text-sm">Rapidgarázs asszisztens</div>
                  <div className="flex items-center gap-1.5 text-xs text-[#E4E4E7]">
                    <div className="w-2 h-2 bg-[#DC2626] rounded-full pulse-dot" />
                    Online · azonnali válasz
                  </div>
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 chat-scroll bg-[#F5F5F5]" data-testid="chat-history">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-msg-${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#0F0F0F] text-[#FFFFFF]" : "bg-white text-[#0F0F0F] border border-[#E4E4E7]"
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#E4E4E7] px-4 py-2.5 text-sm flex items-center gap-2">
                    <Spinner size={16} className="animate-spin" /> Gondolkodom...
                  </div>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-[#E4E4E7]">
              <a href="#callback" className="block text-center text-xs text-[#525252] hover:text-[#0F0F0F] mb-2">
                Inkább visszahívást kérek <ArrowRight size={12} className="inline" />
              </a>
              <div className="flex gap-2">
                <input
                  data-testid="chat-input"
                  className="input-sharp flex-1"
                  placeholder="Írjon üzenetet..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  disabled={loading}
                />
                <button
                  data-testid="chat-send"
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="cta-primary px-4 disabled:opacity-50"
                >
                  <PaperPlaneRight size={18} weight="fill" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
